import React from 'react';
import { Link } from 'react-router-dom';
import useScrollAnimation from '../hooks/useScrollAnimation';
import ContactForm from '../components/ContactForm';
import siteContent from '../content';

const { hero, tips, process, whyUs, firstTimeBuyer, faq, contact } = siteContent.pages.buyers.sections;

const Buyers: React.FC = () => {
  const tipsSection = useScrollAnimation({ threshold: 0 });
  const processSection = useScrollAnimation({ threshold: 0 });
  const benefitsSection = useScrollAnimation({ threshold: 0 });
  const faqSection = useScrollAnimation({ threshold: 0 });
  const contactSection = useScrollAnimation({ threshold: 0 });

  return (
    <>
      <title>Buyer Services | Kline Real Estate Works</title>
      <meta
        name="description"
        content="Expert buyer representation in Royersford, PA. We'll guide you through every step of finding and purchasing your perfect home."
      />
      <meta property="og:title" content="Buyer Services | Kline Real Estate Works" />
      <meta property="og:description" content="Expert buyer representation in Royersford, PA. We'll guide you through every step of finding and purchasing your perfect home." />
      <meta property="og:image" content="https://keithklinegroup.com/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://keithklinegroup.com/og-image.jpg" />

      {/* Hero Section */}
      <section
        className="hero hero-with-image"
        data-cms-page="buyers"
        data-cms-section="hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 43, 92, 0.75), rgba(0, 43, 92, 0.85)), url('${hero.image}')`
        }}
        data-cms-field="image"
        data-cms-type="image"
      >
        <div className="hero-content">
          <p className="hero-subtitle" data-cms-field="subtitle">{hero.subtitle}</p>
          <h1 data-cms-field="heading">{hero.heading}</h1>
          <p data-cms-field="description">{hero.description}</p>
          <Link to={hero.ctaLink} className="btn btn-primary btn-lg" data-cms-field="ctaText" data-cms-link-field="ctaLink">{hero.ctaText}</Link>
        </div>
      </section>

      {/* Buying Tips Section */}
      <section className="section" data-cms-page="buyers" data-cms-section="tips">
        <div className="container">
          <div
            ref={tipsSection.ref}
            className={`scroll-fade-up ${tipsSection.isVisible ? 'visible' : ''}`}
          >
            <div className="two-column">
              <div>
                <h2 data-cms-field="heading">{tips.heading}</h2>
                <div data-cms-list="paragraphs">
                  {tips.paragraphs.map((paragraph, index) => (
                    <p
                      key={index}
                      style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--color-gray-700)' }}
                      data-cms-field="paragraphs"
                      data-cms-index={index}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
              <div>
                <div className="card" style={{ padding: '2rem', backgroundColor: 'var(--color-navy)', color: 'white' }}>
                  <h3 style={{ color: 'var(--color-gold)', marginBottom: '1.5rem' }} data-cms-field="benefitsTitle">{tips.benefitsTitle}</h3>
                  <ul style={{ listStyle: 'none', padding: 0 }} data-cms-list="benefits">
                    {tips.benefits.map((benefit, index) => (
                      <li
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.75rem',
                          marginBottom: '1rem'
                        }}
                        data-cms-index={index}
                      >
                        <span className="material-icons" style={{ color: 'var(--color-gold)', fontSize: '1.25rem', flexShrink: 0 }}>
                          check_circle
                        </span>
                        <span style={{ fontSize: '0.95rem' }} data-cms-field="benefits" data-cms-index={index}>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section" data-cms-page="buyers" data-cms-section="process">
        <div className="container">
          <div
            ref={processSection.ref}
            className={`scroll-fade-up ${processSection.isVisible ? 'visible' : ''}`}
          >
            <div className="section-header">
              <h2 data-cms-field="heading">{process.heading}</h2>
              <p data-cms-field="subheading">{process.subheading}</p>
            </div>
            <div className="services-grid" data-cms-list="steps">
              {process.steps.map((step, index) => (
                <div
                  key={index}
                  className="service-card"
                  data-cms-index={index}
                >
                  <span className="material-icons service-icon" data-cms-field="steps" data-cms-index={index} data-cms-item-field="icon" data-cms-field-type="icon">{step.icon}</span>
                  <h3 data-cms-field="steps" data-cms-index={index} data-cms-item-field="title">{step.title}</h3>
                  <p data-cms-field="steps" data-cms-index={index} data-cms-item-field="description">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-bg" data-cms-page="buyers" data-cms-section="whyUs">
        <div className="container">
          <div
            ref={benefitsSection.ref}
            className={`scroll-fade-up ${benefitsSection.isVisible ? 'visible' : ''}`}
          >
            <div className="two-column">
              <div>
                <h2 style={{ textAlign: 'center' }} data-cms-field="heading">{whyUs.heading}</h2>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                <ul style={{ listStyle: 'none', padding: 0, display: 'inline-block' }} data-cms-list="benefits">
                  {whyUs.benefits.map((benefit, index) => (
                    <li
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        marginBottom: '1rem'
                      }}
                      data-cms-index={index}
                    >
                      <span className="material-icons" style={{ color: 'var(--color-gold)', fontSize: '1.5rem' }}>
                        check_circle
                      </span>
                      <span data-cms-field="benefits" data-cms-index={index}>{benefit}</span>
                    </li>
                  ))}
                </ul>
                </div>
                <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                  <Link to="/contact" className="btn btn-primary">Start Your Search</Link>
                </div>
              </div>
              <div data-cms-page="buyers" data-cms-section="firstTimeBuyer">
                <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
                  <h3 style={{ marginBottom: '1rem' }} data-cms-field="heading">{firstTimeBuyer.heading}</h3>
                  <div data-cms-list="paragraphs">
                    {firstTimeBuyer.paragraphs.map((paragraph, index) => (
                      <p
                        key={index}
                        style={{ marginBottom: index === firstTimeBuyer.paragraphs.length - 1 ? '1.5rem' : undefined }}
                        data-cms-field="paragraphs"
                        data-cms-index={index}
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  <Link to={firstTimeBuyer.ctaLink} className="btn btn-secondary" data-cms-field="ctaText" data-cms-link-field="ctaLink">{firstTimeBuyer.ctaText}</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section" data-cms-page="buyers" data-cms-section="faq">
        <div className="container">
          <div
            ref={faqSection.ref}
            className={`scroll-fade-up ${faqSection.isVisible ? 'visible' : ''}`}
          >
            <div className="section-header">
              <h2 data-cms-field="heading">{faq.heading}</h2>
            </div>
            <div style={{ maxWidth: '800px', margin: '0 auto' }} data-cms-list="items">
              {faq.items.map((item, index) => (
                <div
                  key={index}
                  style={{
                    padding: '1.5rem',
                    borderBottom: index < faq.items.length - 1 ? '1px solid var(--color-gray-200)' : 'none'
                  }}
                  data-cms-index={index}
                >
                  <h4 style={{ marginBottom: '0.5rem' }} data-cms-field="items" data-cms-index={index} data-cms-item-field="question">{item.question}</h4>
                  <p style={{ color: 'var(--color-gray-600)', marginBottom: 0 }} data-cms-field="items" data-cms-index={index} data-cms-item-field="answer">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section section-bg">
        <div className="container">
          <div
            ref={contactSection.ref}
            className={`scroll-scale ${contactSection.isVisible ? 'visible' : ''}`}
            style={{ maxWidth: '700px', margin: '0 auto' }}
          >
            <ContactForm
              title={contact.title}
              description={contact.description}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default Buyers;
